import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useImperativeHandle } from 'react';
import { getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { Heading } from '@skatteetaten/ds-typography';
import './styles.css';


const getPanelColorDefault = ()=>'ochre';
const getPanelVariantDefault = ()=>'outline';
const getPanelPaddingDefault = ()=>'xl';
const getPanelSpacingDefault = ()=>'xxs';
const getPanelTitleAsDefault = ()=>'h3';
const getPanelSubtitleAsDefault = ()=>'h4';

var styles = {
    "panel": "Panel-module_panel__-9ge2",
    "panel_outline": "Panel-module_panel_outline__mX6a-",
    "panel_filled": "Panel-module_panel_filled__VraJr",
    "panel_graphicImage": "Panel-module_panel_graphicImage__ACnYz",
    "panel_graphicIcon": "Panel-module_panel_graphicIcon__RpYCZ",
    "panel_graphicIconHide": "Panel-module_panel_graphicIconHide__Jkj33",
    "panelGraphic": "Panel-module_panelGraphic__Xhw5b",
    "panelGraphicHide": "Panel-module_panelGraphicHide__MPzs8",
    "panelArticle": "Panel-module_panelArticle__Uk45b",
    "panelImage": "Panel-module_panelImage__hgDqf",
    "panel_ochre": "Panel-module_panel_ochre__HIRZo",
    "panel_forest": "Panel-module_panel_forest__4SBlG",
    "panel_burgundy": "Panel-module_panel_burgundy__-FbVL",
    "panel_denim": "Panel-module_panel_denim__gEyTU",
    "panel_graphite": "Panel-module_panel_graphite__vmgB1",
    "panel_paddingS": "Panel-module_panel_paddingS__IRxS1",
    "panel_paddingM": "Panel-module_panel_paddingM__a0PqK",
    "panel_paddingL": "Panel-module_panel_paddingL__C2S17",
    "panel_paddingXL": "Panel-module_panel_paddingXL__r6hPv",
    "panel_paddingXXL": "Panel-module_panel_paddingXXL__qE9RW",
    "panel_paddingMEGA": "Panel-module_panel_paddingMEGA__Wxj0B",
    "panel_spacingXXS": "Panel-module_panel_spacingXXS__yhAjz",
    "panel_spacingXS": "Panel-module_panel_spacingXS__JLmvH",
    "panel_spacingS": "Panel-module_panel_spacingS__4fSVN",
    "panel_spacingM": "Panel-module_panel_spacingM__5FSmc",
    "panel_spacingL": "Panel-module_panel_spacingL__Z6f74",
    "panel_spacingXL": "Panel-module_panel_spacingXL__MfZ-5",
    "panel_spacingXXL": "Panel-module_panel_spacingXXL__FA-6X",
    "panel_spacingMEGA": "Panel-module_panel_spacingMEGA__dX5FH",
    "panelResponsive": "Panel-module_panelResponsive__nXrcR",
    "srOnly": "Panel-module_srOnly__0NUnv"
};

const Panel = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, headingRef, canManuallySetTitleFocus, color = getPanelColorDefault(), classNames, imageSource, imageSourceAltText, hasResponsivePadding, padding = getPanelPaddingDefault(), spacing = getPanelSpacingDefault(), subtitle, subtitleAs = getPanelSubtitleAsDefault(), title, titleAs = getPanelTitleAsDefault(), variant = getPanelVariantDefault(), hideGraphicMobile, hideSubtitle, hideTitle, renderIcon, children }, ref)=>{
    const panelVariantClassName = variant === 'filled' ? styles.panel_filled : styles.panel_outline;
    const panelColorClassName = styles[`panel_${color}`];
    const panelPaddingClassName = styles[`panel_padding${padding.toUpperCase()}`];
    const panelSpacingClassName = styles[`panel_spacing${spacing.toUpperCase()}`];
    let panelGraphicClassName = '';
    if (imageSource) {
        panelGraphicClassName = styles.panel_graphicImage;
    } else if (renderIcon) {
        panelGraphicClassName = `${styles.panel_graphicIcon} ${hideGraphicMobile ? styles.panel_graphicIconHide : ''}`.trim();
    }
    const panelPaddingResponsiveClassName = hasResponsivePadding ? styles.panelResponsive : '';
    const panelCustomClassNames = classNames?.padding ?? '';
    const panelClassName = `${styles.panel} ${panelVariantClassName} ${panelColorClassName} ${panelPaddingClassName} ${panelPaddingResponsiveClassName} ${panelSpacingClassName} ${panelGraphicClassName} ${panelCustomClassNames} ${className}`.trim();
    const graphicClassName = `${styles.panelGraphic} ${hideGraphicMobile ? styles.panelGraphicHide : ''}`.trim();
    const iconClassName = `${renderIcon ? graphicClassName : ''}`.trim();
    const panelHeadingRef = useRef(null);
    useImperativeHandle(headingRef, ()=>panelHeadingRef?.current);
    return /*#__PURE__*/ jsxs("div", {
        ref: ref,
        id: id,
        className: panelClassName,
        lang: lang,
        "data-testid": dataTestId,
        children: [
            !renderIcon && imageSource && /*#__PURE__*/ jsx("div", {
                className: graphicClassName,
                children: /*#__PURE__*/ jsx("img", {
                    src: imageSource,
                    alt: imageSourceAltText ?? '',
                    className: styles.panelImage
                })
            }),
            !imageSource && renderIcon && /*#__PURE__*/ jsx("div", {
                className: iconClassName,
                children: renderIcon?.()
            }),
            /*#__PURE__*/ jsxs("div", {
                className: styles.panelArticle,
                children: [
                    title && /*#__PURE__*/ jsx(Heading, {
                        ref: panelHeadingRef,
                        as: titleAs,
                        level: 3,
                        className: hideTitle ? styles.srOnly : '',
                        hasSpacing: !subtitle || hideSubtitle,
                        canBeManuallyFocused: canManuallySetTitleFocus,
                        children: title
                    }),
                    subtitle && /*#__PURE__*/ jsx(Heading, {
                        as: subtitleAs,
                        level: 5,
                        className: hideSubtitle ? styles.srOnly : '',
                        hasSpacing: true,
                        children: subtitle
                    }),
                    children
                ]
            })
        ]
    });
});
Panel.displayName = 'Panel';

export { Panel, getPanelColorDefault, getPanelPaddingDefault, getPanelSpacingDefault, getPanelSubtitleAsDefault, getPanelTitleAsDefault, getPanelVariantDefault };
